import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';

export type Tab = {
  id: string;
  label: string;
  content: React.ReactNode;
};

export type TabsProps = {
  tabs: Tab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  primaryColor?: string;
  whitelabelEnabled?: boolean;
  className?: string;
};

type TabButtonProps = {
  isActive: boolean;
  primaryColor?: string;
  whitelabelEnabled?: boolean;
};

const TabsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const TabList = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 24px;
`;

const TabButton = styled.button<TabButtonProps>`
  background: transparent;
  border: none;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  font-family: var(--font-bold);
  line-height: 45px;
  color: ${props => props.isActive 
    ? (props.whitelabelEnabled && props.primaryColor ? props.primaryColor : 'var(--pri-02)') 
    : '#666'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background-color: ${props => props.isActive 
      ? (props.whitelabelEnabled && props.primaryColor ? props.primaryColor : 'var(--pri-02)') 
      : 'transparent'};
    transition: background-color 0.2s ease;
  }

  &:hover {
    color: ${props => props.whitelabelEnabled && props.primaryColor ? props.primaryColor : 'var(--pri-02)'};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.whitelabelEnabled && props.primaryColor ? props.primaryColor : 'var(--pri-02)'};
    outline-offset: -2px;
  }
`;

const TabContent = styled.div`
  width: 100%;
`;

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTab: controlledActiveTab,
  onTabChange,
  primaryColor,
  whitelabelEnabled = false,
  className,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<string>(tabs[0]?.id || '');

  // Use controlled value if provided, otherwise use internal state
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  const handleTabClick = useCallback((tabId: string) => {
    if (controlledActiveTab === undefined) {
      setInternalActiveTab(tabId);
    }
    onTabChange?.(tabId);
  }, [controlledActiveTab, onTabChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, tabId: string) => {
    const currentIndex = tabs.findIndex(tab => tab.id === tabId);
    
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleTabClick(tabId);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        // Move to previous tab (wrap to last if at first)
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        const prevTab = tabs[prevIndex];
        handleTabClick(prevTab.id);
        document.getElementById(`tab-${prevTab.id}`)?.focus();
        break;
      case 'ArrowRight':
        e.preventDefault();
        // Move to next tab (wrap to first if at last)
        const nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        const nextTab = tabs[nextIndex];
        handleTabClick(nextTab.id);
        document.getElementById(`tab-${nextTab.id}`)?.focus();
        break;
      case 'Home':
        e.preventDefault();
        // Move to first tab
        handleTabClick(tabs[0].id);
        document.getElementById(`tab-${tabs[0].id}`)?.focus();
        break;
      case 'End':
        e.preventDefault();
        // Move to last tab
        const lastTab = tabs[tabs.length - 1];
        handleTabClick(lastTab.id);
        document.getElementById(`tab-${lastTab.id}`)?.focus();
        break;
    }
  }, [tabs, handleTabClick]);

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <TabsContainer className={className}>
      <TabList role="tablist">
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            role="tab"
            aria-selected={tab.id === activeTab}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
            tabIndex={tab.id === activeTab ? 0 : -1}
            isActive={tab.id === activeTab}
            primaryColor={primaryColor}
            whitelabelEnabled={whitelabelEnabled}
            onClick={() => handleTabClick(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, tab.id)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabList>
      <TabContent
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {activeTabContent}
      </TabContent>
    </TabsContainer>
  );
};
